import React from "react";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import { Box, Button, Grid, InputLabel, Typography, useMediaQuery } from "@mui/material";
import { Add, ContentCopy, DeleteOutline } from "@mui/icons-material";

import { CustomInput } from "../../../../../../components/Custom/Input";
import CustomHelper from "../../../../../../components/Helper/CustomHelper";
import { ALERT_DIALOG } from "../../../../../../store/constants/auth";
import { getCssVariableHandler } from "../../../../../../utils/styles/getCssVariables";
import styles from "../../sessions.module.css";
import { rowErrorFor } from "./positionForm.errors";
import type { PositionFormik, SessionDates, ShiftTimeInfo } from "./positionForm.types";
import { OvernightCheckbox } from "./PositionFormFields";
import ShiftTimeCells from "./ShiftTimeCells";
import {
  createEmptyShiftRow,
  getSessionDateKeys,
  removeShiftRow,
  withDateStart,
  withOvernight,
} from "./positionForm.utils";

const fontRgb = () => getCssVariableHandler({ variableName: "--font-rgb" });
const pinkRgb = () => getCssVariableHandler({ variableName: "--primary-pink-rgb" });

const actionButtonSx = (background: string) => ({
  borderRadius: "10px",
  background,
  boxShadow: "0px 0px 20px 0px rgba(209, 117, 182, 0.40)",
  minWidth: "auto",
  padding: "5px !important",
});

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Grid item sx={{ width: "100%", paddingTop: "0 !important" }}>
    <Typography>{children}</Typography>
  </Grid>
);

/**
 * Add flow: one row per date (`values.positionTimeInfo`). In the list view with
 * "each date of the session" on, the first row stands for the whole session and
 * edits every date row at once; extra rows after them are independent.
 */
const ShiftRowsList = ({ formik, session }: { formik: PositionFormik; session: SessionDates }) => {
  const { values, setValues, setFieldValue, handleBlur } = formik;
  const dispatch = useDispatch();
  const isMidWidth = useMediaQuery("(max-width:1000px) and (min-width:930px)");
  const isNarrow = useMediaQuery("(max-width:930px)");

  const rows = values?.positionTimeInfo ?? [];
  const totalDates = getSessionDateKeys(session).length;
  const isCalendarView = values?.currentView === "calendar";
  const eachDate = !!values?.eachDateOfSession;

  /** True when row 0 stands for every date of the session (list view only). */
  const isSessionWideRow = (rowIndex: number) =>
    eachDate && rowIndex === 0 && !isCalendarView && !values?.detachEachDateOfSessionInput;

  const updateRows = (rowIndex: number, update: (row: ShiftTimeInfo) => ShiftTimeInfo) => {
    const inScope = isSessionWideRow(rowIndex)
      ? (idx: number) => idx < totalDates
      : (idx: number) => idx === rowIndex;
    setValues((prev) => ({
      ...prev,
      positionTimeInfo: prev?.positionTimeInfo?.map((row, idx) => (inScope(idx) ? update(row) : row)),
    }));
  };

  const confirmDelete = (rowIndex: number) =>
    dispatch({
      type: ALERT_DIALOG,
      payload: {
        open: true,
        title: "Confirmation alert!",
        message: "Are you sure you want to delete this position?",
        onOk: () => setValues((prev) => removeShiftRow(prev, rowIndex)),
      },
    });

  const columnWidth = isNarrow ? "24%" : "22%";

  return (
    <>
      {rows.map((row, rowIndex) => {
        // In the list view the per-date rows are hidden behind the session-wide row.
        const isHiddenDateRow = !isCalendarView && eachDate && rowIndex > 0 && rowIndex < totalDates;
        if (isHiddenDateRow) return null;

        const isFirst = rowIndex === 0;
        const errorFor = rowErrorFor(formik, rowIndex);
        const canCopy = isCalendarView
          ? !eachDate || rowIndex + 1 > (values?.calendarViewDates?.length ?? 0)
          : !eachDate || rowIndex + 1 > totalDates;
        const canAdd = eachDate && (isCalendarView ? rowIndex + 1 === totalDates : isFirst);
        const canDelete = rows.length > 1 || isCalendarView;

        return (
          <React.Fragment key={rowIndex}>
            {eachDate && isFirst ? <SectionTitle>All session dates: </SectionTitle> : null}
            {eachDate && rowIndex === totalDates ? (
              <SectionTitle>Additional entries: </SectionTitle>
            ) : null}

            <ShiftTimeCells
              shift={row}
              namePrefix={`positionTimeInfo[${rowIndex}].`}
              session={session}
              onShiftChange={(update) => updateRows(rowIndex, update)}
              onDateChange={(dateKey) =>
                setFieldValue(`positionTimeInfo[${rowIndex}]`, withDateStart(row, dateKey))
              }
              onBlur={handleBlur}
              errorFor={errorFor}
              dateLabel={!isCalendarView && !isFirst && rowIndex < totalDates ? "Date Start" : "Date"}
              showDateLabel={eachDate || isFirst}
              showTimeEndLabel={isFirst}
              dateContent={
                isSessionWideRow(rowIndex) ? (
                  <Box sx={{ display: "flex", alignItems: "center", height: "50px" }}>
                    <Typography>
                      {dayjs(session?.sessionStart).format("ddd MM/DD/YYYY")} - <br />
                      {dayjs(session?.sessionEnd)?.format("ddd MM/DD/YYYY")}
                    </Typography>
                  </Box>
                ) : undefined
              }
              widths={{
                date: { width: columnWidth },
                time: { width: columnWidth },
                hours: { width: isNarrow ? "28%" : "44%" },
              }}
            />

            <Grid item sx={{ width: isNarrow ? "13%" : "9%" }}>
              <Box className={styles["form-box"]}>
                <InputLabel sx={{ visibility: isFirst ? "visible" : "hidden" }}>Qty</InputLabel>
                <CustomInput
                  type="number"
                  fullWidth
                  placeholder="10"
                  name={`positionTimeInfo[${rowIndex}].quantity`}
                  value={row?.quantity}
                  onBlur={handleBlur}
                  onChange={(e) => {
                    const quantity = e?.target?.value;
                    updateRows(rowIndex, (current) => ({ ...current, quantity }));
                  }}
                />
                {errorFor("quantity")}
              </Box>
            </Grid>

            <Grid item sx={{ width: isNarrow || isMidWidth ? "13%" : "11.5%" }}>
              <OvernightCheckbox
                name={`positionTimeInfo[${rowIndex}].overNightShift`}
                checked={row?.overNightShift}
                hideLabel={!isFirst}
                onBlur={handleBlur}
                onChange={(overnight) =>
                  updateRows(rowIndex, (current) => withOvernight(current, overnight))
                }
                error={errorFor("overNightShift")}
              />
            </Grid>

            <Grid
              item
              sx={{
                width: isNarrow ? "100%" : isMidWidth ? "11.5%" : "13%",
                flexGrow: "1",
                position: "relative",
              }}
            >
              <Box className={styles["form-box"]} sx={{ justifyContent: "flex-end" }}>
                <InputLabel sx={{ visibility: "hidden" }}>Actions</InputLabel>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: "0.5rem",
                  }}
                >
                  {canCopy ? (
                    <CustomHelper message="Copy Row">
                      <Button
                        sx={actionButtonSx(fontRgb())}
                        onClick={() => setFieldValue("positionTimeInfo", [...rows, { ...row }])}
                      >
                        <ContentCopy sx={{ fill: pinkRgb() }} />
                      </Button>
                    </CustomHelper>
                  ) : null}

                  {canAdd ? (
                    <CustomHelper message="Add Row">
                      <Button
                        sx={actionButtonSx(fontRgb())}
                        onClick={() =>
                          setFieldValue("positionTimeInfo", [...rows, createEmptyShiftRow()])
                        }
                      >
                        <Add sx={{ fill: pinkRgb() }} />
                      </Button>
                    </CustomHelper>
                  ) : null}

                  {canDelete ? (
                    <CustomHelper message="Delete Row">
                      <Button sx={actionButtonSx(pinkRgb())} onClick={() => confirmDelete(rowIndex)}>
                        <DeleteOutline />
                      </Button>
                    </CustomHelper>
                  ) : null}
                </Box>
              </Box>
            </Grid>
          </React.Fragment>
        );
      })}
    </>
  );
};

export default ShiftRowsList;
